import { createLogger } from '@svs/nova-core/log';
import { type StateCreator, type StoreMutatorIdentifier } from 'zustand';

const logger = createLogger('poolbetting.zustand');

type Logger = <
  TState,
  TMps extends [StoreMutatorIdentifier, unknown][] = [],
  TMcs extends [StoreMutatorIdentifier, unknown][] = [],
>(
  f: StateCreator<TState, TMps, TMcs>,
  name?: string
) => StateCreator<TState, TMps, TMcs>;

type LoggerImpl = <TState>(f: StateCreator<TState>, name?: string) => StateCreator<TState>;

const loggerImpl: LoggerImpl = (f, loggerName) => (set, get, store) => {
  const loggedSet: typeof set = (...a) => {
    set(...(a as Parameters<typeof set>));
    logger.debug('State updated', { name: loggerName, state: get() });
  };
  const { setState } = store;
  store.setState = (...a): void => {
    setState(...(a as Parameters<typeof setState>));
    logger.debug('Store state updated', { name: loggerName, state: store.getState() });
  };

  return f(loggedSet, get, store);
};

/**
 * Zustand middleware logging activities in state.
 *
 * Inspiration taken from:
 * https://zustand.docs.pmnd.rs/guides/typescript#authoring-middlewares-and-advanced-usage
 *
 * Implementation uses higher-kinded mutator:
 * https://github.com/pmndrs/zustand/issues/710
 *
 * @param f Zustand store API
 * @param name Logger name to to use while logging
 * @returns Enhanced store API with logger middleware
 */
export const loggerMiddleware = loggerImpl as unknown as Logger;
